/**
 * Command Palette Component
 * Per App-Shell.md Section Global Components and Design-System.md Section 7.5
 *
 * Extended with finance search (Block 4 FD1):
 * - Live API search for customers, vouchers, ledgers when typing 2+ chars
 * - Finance quick actions: Alt+P (Payment), Alt+R (Receipt), Alt+J (Journal)
 * - Customer/Vendor click → Quick Lookup Modal
 * - Voucher click → /finance/transactions?selected=ID
 * - Ledger click → /finance/reports/general-ledger?ledgerId=ID
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  LayoutDashboard,
  FileText,
  Package,
  Users,
  Settings,
  Plus,
  Play,
  Clock,
  Search,
  Loader2,
  BookOpen,
  Banknote,
  ArrowDownLeft,
  ArrowUpRight,
} from 'lucide-react'
import { toast } from 'sonner'

import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  CommandShortcut,
} from '@/components/ui/command'
import { useUIStore } from '@/stores/ui-store'
import { useRecentItemsStore, type RecentItem } from '@/stores/recent-items-store'
import { navigationCommands, actionCommands, commandsFor } from './commands'
import { useUserPermissions } from '@/hooks/useHasPermission'
import { useFinanceSearch } from '@/features/finance/hooks/useFinanceSearch'
import { QuickLookupModal } from '@/features/finance/components/global/QuickLookupModal'

// Icon map for dynamic icon rendering
const iconMap = {
  LayoutDashboard,
  FileText,
  Package,
  Users,
  Settings,
  Plus,
  Play,
  Clock,
  Search,
} as const

type IconName = keyof typeof iconMap

function getIcon(iconName: string | undefined) {
  if (!iconName) return null
  const Icon = iconMap[iconName as IconName]
  return Icon ? <Icon className="size-4" /> : null
}

// Icon for recent item types
function getRecentItemIcon(type: RecentItem['type']) {
  switch (type) {
    case 'quotation':
      return <FileText className="size-4" />
    case 'customer':
      return <Users className="size-4" />
    case 'job_order':
      return <Package className="size-4" />
    default:
      return <Clock className="size-4" />
  }
}

export function CommandPalette() {
  const navigate = useNavigate()
  const { commandPaletteOpen, setCommandPaletteOpen } = useUIStore()
  const { items: recentItems } = useRecentItemsStore()
  // Navigation entries the menu also hides from this person (Customers without the list permission).
  const userPermissions = useUserPermissions()
  const visibleNavigationCommands = useMemo(
    () => commandsFor(navigationCommands, userPermissions),
    [userPermissions],
  )
  const [searchQuery, setSearchQuery] = useState('')

  // Finance search (debounced, triggers after 2+ chars)
  const { results: financeResults, isSearching, hasResults: hasFinanceResults } =
    useFinanceSearch(searchQuery)

  // Quick Lookup Modal state
  const [lookupSubledgerId, setLookupSubledgerId] = useState<string | null>(null)

  // "G then X" shortcut state (vim-style navigation)
  const [gKeyPressed, setGKeyPressed] = useState(false)

  // Handle palette open/close with search reset
  const handleOpenChange = useCallback(
    (open: boolean) => {
      setCommandPaletteOpen(open)
      if (!open) setSearchQuery('')
    },
    [setCommandPaletteOpen],
  )

  // Get top 5 recent items
  const displayedRecentItems = useMemo(() => {
    return recentItems.slice(0, 5)
  }, [recentItems])

  // Handle command selection
  const handleSelect = useCallback(
    (commandId: string) => {
      // Close the palette first
      setCommandPaletteOpen(false)

      // Handle navigation commands
      const navCommand = navigationCommands.find((c) => c.id === commandId)
      if (navCommand?.href) {
        navigate(navCommand.href)
        return
      }

      // Handle action commands
      const actionCommand = actionCommands.find((c) => c.id === commandId)
      if (actionCommand) {
        switch (actionCommand.id) {
          case 'action-new-quote':
            toast.info('Create quotation coming soon', {
              description: 'This feature is under development',
            })
            break
          case 'action-focus-mode':
            toast.info('Focus Mode is coming soon!', {
              description: 'Process tasks efficiently, one after another',
            })
            break
          // Finance quick actions
          case 'action-new-payment':
            navigate('/finance/transactions?action=new-payment')
            break
          case 'action-new-receipt':
            navigate('/finance/transactions?action=new-receipt')
            break
          case 'action-new-journal':
            navigate('/finance/transactions?action=new-journal')
            break
          case 'action-new-credit-note':
            navigate('/finance/transactions?action=new-credit-note')
            break
          case 'action-new-debit-note':
            navigate('/finance/transactions?action=new-debit-note')
            break
          case 'action-new-sales-invoice':
            navigate('/finance/transactions?action=new-sales-invoice')
            break
          case 'action-new-purchase-invoice':
            navigate('/finance/transactions?action=new-purchase-invoice')
            break
        }
        return
      }

      // Handle finance search results
      if (commandId.startsWith('finance-customer-') || commandId.startsWith('finance-vendor-')) {
        const subledgerId = commandId.replace(/^finance-(customer|vendor)-/, '')
        setLookupSubledgerId(subledgerId)
        return
      }
      if (commandId.startsWith('finance-voucher-')) {
        const voucherId = commandId.replace('finance-voucher-', '')
        navigate(`/finance/transactions?selected=${voucherId}`)
        return
      }
      if (commandId.startsWith('finance-ledger-')) {
        const ledgerId = commandId.replace('finance-ledger-', '')
        navigate(`/finance/reports/general-ledger?ledgerId=${ledgerId}`)
        return
      }

      // Handle recent items
      if (commandId.startsWith('recent-')) {
        const itemId = commandId.replace('recent-', '')
        const recentItem = recentItems.find((item) => item.id === itemId)
        if (recentItem) {
          navigate(recentItem.href)
        }
      }
    },
    [navigate, setCommandPaletteOpen, recentItems]
  )

  // Track input value
  const handleInputCapture = useCallback(() => {
    // Use a microtask to read the input value after cmdk processes it
    queueMicrotask(() => {
      const input = document.querySelector('[cmdk-input]') as HTMLInputElement | null
      if (input) {
        setSearchQuery(input.value)
      }
    })
  }, [])

  // Global keyboard shortcut handler
  useEffect(() => {
    let gKeyTimeout: ReturnType<typeof setTimeout> | null = null

    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't capture shortcuts when typing in inputs (except Cmd+K)
      const target = e.target as HTMLElement
      const isInput = target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable

      // Cmd+K or Ctrl+K to open
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        setCommandPaletteOpen(!commandPaletteOpen)
        return
      }

      // Finance shortcuts (Option/Alt + P/R/J) — only when not typing in input
      // Use e.code instead of e.key because Option+letter on Mac produces special characters
      if (e.altKey && !isInput) {
        switch (e.code) {
          case 'KeyP':
            e.preventDefault()
            navigate('/finance/transactions?action=new-payment')
            return
          case 'KeyR':
            e.preventDefault()
            navigate('/finance/transactions?action=new-receipt')
            return
          case 'KeyJ':
            e.preventDefault()
            navigate('/finance/transactions?action=new-journal')
            return
        }
      }

      // "G then X" vim-style navigation shortcuts (only when not in input and palette closed)
      if (!isInput && !commandPaletteOpen && !e.metaKey && !e.ctrlKey && !e.altKey) {
        // Check if this is the second key after G
        if (gKeyPressed) {
          setGKeyPressed(false)
          if (gKeyTimeout) clearTimeout(gKeyTimeout)

          switch (e.key.toLowerCase()) {
            case 'd':
              e.preventDefault()
              navigate('/finance/dashboard')
              return
            case 't':
              e.preventDefault()
              navigate('/finance/transactions')
              return
            case 'a':
              e.preventDefault()
              navigate('/finance/allocations')
              return
            case 'r':
              e.preventDefault()
              navigate('/finance/reports')
              return
          }
        }

        // Check if G was pressed to start sequence
        if (e.key.toLowerCase() === 'g') {
          setGKeyPressed(true)
          // Reset after 1 second if no follow-up key
          gKeyTimeout = setTimeout(() => setGKeyPressed(false), 1000)
          return
        }
      }

    }

    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      if (gKeyTimeout) clearTimeout(gKeyTimeout)
    }
  }, [commandPaletteOpen, setCommandPaletteOpen, navigate, gKeyPressed])

  return (
    <>
      <CommandDialog
        open={commandPaletteOpen}
        onOpenChange={handleOpenChange}
        title="Command Palette"
        description="Search for a command or navigate quickly"
        showCloseButton={false}
      >
        <CommandInput
          placeholder="Search anything..."
          onInput={handleInputCapture}
          onKeyDown={handleInputCapture}
        />
        <CommandList className="max-h-[400px]">
          <CommandEmpty>
            {isSearching ? (
              <div className="flex items-center justify-center gap-2 py-2">
                <Loader2 className="size-4 animate-spin" />
                <span>Searching...</span>
              </div>
            ) : (
              'No results found.'
            )}
          </CommandEmpty>

          {/* Finance API search results — show when we have results */}
          {hasFinanceResults && financeResults && (
            <>
              {financeResults.customers.length > 0 && (
                <CommandGroup heading="Subledger Accounts">
                  {financeResults.customers.map((item) => (
                    <CommandItem
                      key={`finance-${item.type.toLowerCase()}-${item.id}`}
                      value={`finance-${item.type.toLowerCase()}-${item.id}`}
                      keywords={[item.name ?? '', item.code ?? '', item.type]}
                      onSelect={() =>
                        handleSelect(`finance-${item.type.toLowerCase()}-${item.id}`)
                      }
                      forceMount
                      className="cursor-pointer data-[selected=true]:bg-violet-100 data-[selected=true]:text-violet-900 dark:data-[selected=true]:bg-violet-900/30 dark:data-[selected=true]:text-violet-100"
                    >
                      <Users className="size-4" />
                      <div className="flex flex-col gap-0.5 min-w-0 flex-1">
                        <span className="text-sm truncate">{item.name}</span>
                        <span className="text-xs text-muted-foreground">
                          {item.code} &middot; {item.type.replace('_', ' ')}
                        </span>
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}

              {financeResults.vouchers.length > 0 && (
                <CommandGroup heading="Vouchers">
                  {financeResults.vouchers.map((item) => {
                    const VIcon = item.type.includes('RECEIPT')
                      ? ArrowDownLeft
                      : item.type.includes('PAYMENT')
                        ? ArrowUpRight
                        : Banknote
                    return (
                      <CommandItem
                        key={`finance-voucher-${item.id}`}
                        value={`finance-voucher-${item.id}`}
                        keywords={[
                          item.voucherNo ?? '',
                          item.narration ?? '',
                          item.type,
                        ]}
                        onSelect={() => handleSelect(`finance-voucher-${item.id}`)}
                        forceMount
                        className="cursor-pointer data-[selected=true]:bg-violet-100 data-[selected=true]:text-violet-900 dark:data-[selected=true]:bg-violet-900/30 dark:data-[selected=true]:text-violet-100"
                      >
                        <VIcon className="size-4" />
                        <div className="flex flex-col gap-0.5 min-w-0 flex-1">
                          <span className="text-sm font-mono truncate">
                            {item.voucherNo}
                          </span>
                          {item.narration && (
                            <span className="text-xs text-muted-foreground truncate">
                              {item.narration}
                            </span>
                          )}
                        </div>
                        <span className="text-xs text-muted-foreground shrink-0">
                          {item.type.replace(/_/g, ' ')}
                        </span>
                      </CommandItem>
                    )
                  })}
                </CommandGroup>
              )}

              {financeResults.ledgers.length > 0 && (
                <CommandGroup heading="Ledger Accounts">
                  {financeResults.ledgers.map((item) => (
                    <CommandItem
                      key={`finance-ledger-${item.id}`}
                      value={`finance-ledger-${item.id}`}
                      keywords={[item.name ?? '', item.code ?? '']}
                      onSelect={() => handleSelect(`finance-ledger-${item.id}`)}
                      forceMount
                      className="cursor-pointer data-[selected=true]:bg-violet-100 data-[selected=true]:text-violet-900 dark:data-[selected=true]:bg-violet-900/30 dark:data-[selected=true]:text-violet-100"
                    >
                      <BookOpen className="size-4" />
                      <div className="flex flex-col gap-0.5 min-w-0 flex-1">
                        <span className="text-sm truncate">{item.name}</span>
                        <span className="text-xs text-muted-foreground">{item.code}</span>
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}

              <CommandSeparator />
            </>
          )}

          {/* Searching indicator */}
          {isSearching && searchQuery.length >= 2 && !hasFinanceResults && (
            <>
              <CommandGroup heading="Finance Search">
                <CommandItem disabled forceMount>
                  <Loader2 className="size-4 animate-spin" />
                  <span className="text-muted-foreground">Searching finance data...</span>
                </CommandItem>
              </CommandGroup>
              <CommandSeparator />
            </>
          )}

          {/* Recent Items - Only show if there are any */}
          {displayedRecentItems.length > 0 && (
            <>
              <CommandGroup heading="Recent">
                {displayedRecentItems.map((item) => (
                  <CommandItem
                    key={`recent-${item.id}`}
                    value={`recent-${item.id}`}
                    onSelect={() => handleSelect(`recent-${item.id}`)}
                    className="cursor-pointer data-[selected=true]:bg-violet-100 data-[selected=true]:text-violet-900 dark:data-[selected=true]:bg-violet-900/30 dark:data-[selected=true]:text-violet-100"
                  >
                    {getRecentItemIcon(item.type)}
                    <div className="flex flex-col gap-0.5">
                      <span className="font-mono text-sm">{item.title}</span>
                      <span className="text-xs text-muted-foreground">
                        {item.subtitle}
                      </span>
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
              <CommandSeparator />
            </>
          )}

          {/* Navigation Commands */}
          <CommandGroup heading="Navigation">
            {visibleNavigationCommands.map((command) => (
              <CommandItem
                key={command.id}
                value={command.id}
                keywords={command.keywords}
                onSelect={() => handleSelect(command.id)}
                className="cursor-pointer data-[selected=true]:bg-violet-100 data-[selected=true]:text-violet-900 dark:data-[selected=true]:bg-violet-900/30 dark:data-[selected=true]:text-violet-100"
              >
                {getIcon(command.icon)}
                <span>{command.label}</span>
                {command.shortcut && (
                  <CommandShortcut>{command.shortcut}</CommandShortcut>
                )}
              </CommandItem>
            ))}
          </CommandGroup>

          <CommandSeparator />

          {/* Action Commands */}
          <CommandGroup heading="Actions">
            {actionCommands.map((command) => (
              <CommandItem
                key={command.id}
                value={command.id}
                keywords={command.keywords}
                onSelect={() => handleSelect(command.id)}
                className="cursor-pointer data-[selected=true]:bg-violet-100 data-[selected=true]:text-violet-900 dark:data-[selected=true]:bg-violet-900/30 dark:data-[selected=true]:text-violet-100"
              >
                {getIcon(command.icon)}
                <span>{command.label}</span>
                {command.shortcut && (
                  <CommandShortcut>{command.shortcut}</CommandShortcut>
                )}
              </CommandItem>
            ))}
          </CommandGroup>
        </CommandList>
      </CommandDialog>

      {/* Quick Lookup Modal (overlays on top of everything) */}
      <QuickLookupModal
        open={!!lookupSubledgerId}
        subledgerId={lookupSubledgerId}
        onClose={() => setLookupSubledgerId(null)}
      />
    </>
  )
}
